class GiftWithPurchaseManager {
  constructor () {
    this.configs = []
    this.initialized = false
  }

  init () {
    if (this.initialized) return

    try {
      const configElements = document.querySelectorAll('[data-gwp-config]')

      if (configElements.length === 0) {
        console.info('GWP: No configuration found')
        return
      }

      this.configs = Array.from(configElements).map(configElement => ({
        enabled: configElement.dataset.gwpEnabled === 'true',
        threshold: parseInt(configElement.dataset.gwpThreshold) || 6000,
        productId: configElement.dataset.gwpProductId,
        variantId: configElement.dataset.gwpVariantId,
        campaignId: configElement.dataset.gwpCampaignId || 'default',
      }))

      console.info(`GWP: Initialized ${this.configs.length} campaign(s)`, this.configs)
      this.initialized = true
    } catch (err) {
      console.error('GWP: Init failed', err)
    }
  }

  isEnabled () {
    if (!this.initialized) this.init()
    return this.configs.length > 0 && this.configs.some(config => config.enabled && config.variantId)
  }

  process (cartState) {
    if (!cartState?.items) {
      console.warn('GWP: Invalid cart state', cartState)
      return { toAdd: [], toRemove: [] }
    }

    try {
      let cartTotal = 0
      for (const item of cartState.items) {
        if (item.properties?._gwp !== 'true') {
          cartTotal += item.final_line_price || 0
        }
      }

      const existingGifts = this.findGWPItems(cartState.items)
      const giftsToRemove = []

      for (const gift of existingGifts) {
        const giftThreshold = parseInt(gift.properties?._threshold) || 0
        const giftMaxThreshold = parseInt(gift.properties?._max_threshold) || 0
        const giftCampaign = gift.properties?._gwp_campaign || 'unknown'

        const thresholdMet = cartTotal >= giftThreshold
        const maxThresholdExceeded = giftMaxThreshold > 0 && cartTotal > giftMaxThreshold

        if (!thresholdMet) {
          console.info(`GWP: Cart below threshold for campaign "${giftCampaign}", removing gift`, gift.key)
          giftsToRemove.push(gift.key)
        } else if (maxThresholdExceeded) {
          console.info(`GWP: Cart above max threshold for campaign "${giftCampaign}", removing gift`, gift.key)
          giftsToRemove.push(gift.key)
        }
      }

      return {
        toAdd: [],
        toRemove: giftsToRemove,
      }
    } catch (err) {
      console.error('GWP: Process error', err)
      return { toAdd: [], toRemove: [] }
    }
  }

  findGWPItems (cartItems) {
    const gwpItems = cartItems.filter(item => {
      const isGWP = item.properties?._gwp === 'true'
      if (isGWP) {
        console.info('GWP: Found gift item', {
          key: item.key,
          title: item.title,
          campaign: item.properties?._gwp_campaign || 'unknown',
          threshold: item.properties?._threshold,
          max_threshold: item.properties?._max_threshold,
        })
      }
      return isGWP
    })
    return gwpItems
  }

  getState (cartState) {
    if (!this.isEnabled()) {
      return {
        enabled: false,
        campaigns: [],
      }
    }

    const cartTotal = cartState?.total_price || 0
    const gwpItems = this.findGWPItems(cartState?.items || [])

    // Calculate state for each campaign
    const campaignStates = this.configs.map(config => {
      const threshold = config.threshold
      const thresholdMet = cartTotal >= threshold
      const remaining = Math.max(0, threshold - cartTotal)
      const progress = Math.min(100, (cartTotal / threshold) * 100)

      // Check if this campaign has a gift in cart
      const hasGift = gwpItems.some(item =>
        item.properties?._gwp_campaign === config.campaignId,
      )

      return {
        campaignId: config.campaignId,
        enabled: config.enabled,
        thresholdMet,
        progress: Math.round(progress),
        remaining,
        threshold,
        hasGift,
      }
    })

    return {
      enabled: true,
      cartTotal,
      campaigns: campaignStates,
      totalGifts: gwpItems.length,
    }
  }
}

class GiftWithPurchaseElement extends window.BAO.CustomElement() {
  constructor () {
    super()
    this.handleTriggerClick = this.handleTriggerClick.bind(this)
  }

  connectedCallback () {
    super.connectedCallback()
    this.setupTriggers()
  }

  setupTriggers () {
    const triggerButton = this.querySelector('[data-gift-with-purchase-el="drawer_trigger"]')
    if (!triggerButton) return

    const dataElement = this.querySelector('[data-gift-with-purchase-el="opt2_data"]')
    if (!dataElement) return

    this.listeners.add(triggerButton, 'click', this.handleTriggerClick)
  }

  handleTriggerClick (event) {
    event.preventDefault()

    const dataElement = this.querySelector('[data-gift-with-purchase-el="opt2_data"]')

    if (!dataElement) {
      console.warn('GWP: No data element found')
      return
    }

    const detail = {
      productUrl: dataElement.dataset.productUrl,
      productId: dataElement.dataset.productId,
      messageLabel: dataElement.dataset.productMessageLabel,
      messageContent: dataElement.dataset.productMessage,
      customPriceText: dataElement.dataset.customPriceText,
      freeGiftStyling: dataElement.dataset.freeGiftStyling,
      threshold: dataElement.dataset.minCartValue,
      maxThreshold: dataElement.dataset.maxCartValue,
      campaignId: dataElement.dataset.campaignId,
    }

    document.dispatchEvent(new CustomEvent('addGWP', { detail }))
  }
}

if (typeof window.customElements !== 'undefined') {
  window.customElements.define('gift-with-purchase', GiftWithPurchaseElement)
}

window.BAO = window.BAO || {}
window.BAO.GiftWithPurchase = new GiftWithPurchaseManager()

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.BAO.GiftWithPurchase.init()
  })
} else {
  window.BAO.GiftWithPurchase.init()
}
